import React from "react";
import { TabContent, TabPane, Card, CardBody, Row, Col } from "reactstrap";

// Components
import Form from "../../../../../components/base/Form";
import Text from "../../../../../components/base/Text";
import SaveButton from "../../../../../components/base/SaveButton";
import Toast from "../../../../../components/base/Toast";
import SingleCheckbox from "../../../../../components/base/SingleCheckbox";
import { SnowflakeColorIcon } from "../../../../../assets/img/icons";

import { apiClient } from "../../../../../apiClient";
import { endpoints } from "../../../../../configs";
import { isBadRequest } from "../../../../../common/http";
import Spinner from "../../../../../components/base/Spinner";

// ── Main Component ──────────────────────────────────────────────────────────
const SnowflakeSection = props => {
  const activeTab = props && props.activeTab;

  const [isLoading, setIsLoading] = React.useState(false);
  const [selections, setSelections] = React.useState({
    warehouse: "",
    database: "",
    schema: "",
    role: ""
  });

  const [metadata, setMetadata] = React.useState({
    warehouses: [],
    databases: [],
    schemas: [],
    roles: []
  });
  const [integrationData, setIntegrationData] = React.useState(null);
  const [isConnected, setIsConnected] = React.useState(false);

  const fetchIntegration = async () => {
    try {
      setIsLoading(true);
      const response = await apiClient.get(
        `${endpoints().integrationAPI}/snowflake`
      );
      setIsLoading(false);

      if (response && response.data) {
        setIntegrationData(response.data);
        setIsConnected(response.data.is_connected);
      }
    } catch (err) {
      console.warn("Could not load Snowflake integration:", err.message);
      setIsLoading(false);
    }
  };

  React.useEffect(() => {
    fetchIntegration();
  }, []);

  const snowflakeSettingsInitialValues = {
    account:
      (integrationData &&
        integrationData.account_id &&
        integrationData.account_id) ||
      "",
    user_name:
      (integrationData &&
        integrationData.user_name &&
        integrationData.user_name) ||
      "",
    password:
      (integrationData &&
        integrationData.password &&
        integrationData.password) ||
      "",
    warehouse:
      metadata.warehouses.find(
        item =>
          item.value ===
          (integrationData &&
            integrationData.other_configs &&
            integrationData.other_configs.warehouse)
      ) || "",
    database:
      metadata.databases.find(
        item =>
          item.value ===
          (integrationData &&
            integrationData.other_configs &&
            integrationData.other_configs.database)
      ) || "",
    schema:
      metadata.schemas.find(
        item =>
          item.value ===
          (integrationData &&
            integrationData.other_configs &&
            integrationData.other_configs.schema)
      ) || "",
    role:
      metadata.roles.find(
        item =>
          item.value ===
          (integrationData &&
            integrationData.other_configs &&
            integrationData.other_configs.role)
      ) || "",
    sync_partner:
      (integrationData &&
        integrationData.setting &&
        integrationData.setting.sync_partner) ||
      false,
    sync_referral:
      (integrationData &&
        integrationData.setting &&
        integrationData.setting.sync_referral) ||
      false,
    sync_activity:
      (integrationData &&
        integrationData.setting &&
        integrationData.setting.sync_activity) ||
      false
  };

  const fetchMetadata = async database => {
    setMetaLoading(true);

    try {
      const url = database
        ? `${endpoints().snowFlakeAPI}/metadata?database=${database}`
        : `${endpoints().snowFlakeAPI}/metadata`;
      const response = await apiClient.get(url);

      if (response && response.data) {
        setMetadata(prev => ({ ...prev, ...response.data }));
      }
    } catch (err) {
      console.warn("Could not load Snowflake metadata:", err.message);
      isBadRequest(err) && Toast.error(err.response.data.message);
    } finally {
      setMetaLoading(false);
    }
  };

  // React.useEffect(() => {
  //   if (
  //     integrationData &&
  //     integrationData.account_id &&
  //     integrationData.user_name
  //   ) {
  //     fetchMetadata(
  //       integrationData &&
  //         integrationData.other_configs &&
  //         integrationData.other_configs.database
  //     );
  //   }
  // }, [integrationData]);

  const [isConnection, setIsConnection] = React.useState(false);
  const [metaLoading, setMetaLoading] = React.useState(false);

  const handleDropdownChange = (name, value) => {
    setSelections(prev => ({ ...prev, [name]: value }));
    if (name === "database" && value) {
      fetchMetadata(value);
    }
  };

  const submit = async values => {
    try {
      const payload = {
        id: integrationData && integrationData.id,
        account: values.account,
        username: values.user_name,
        password: values.password,
        other_configs: {
          warehouse: values.warehouse.value,
          database: values.database.value,
          schema: values.schema.value,
          role: values.role.value
        },
        sync_settings: {
          sync_partner: values.sync_partner,
          sync_referral: values.sync_referral,
          sync_activity: values.sync_activity
        }
      };

      const response = await apiClient.post(
        `${endpoints().integrationAPI}/snowflake/save`,
        payload
      );

      if (response && response.status < 300) {
        Toast.success("Snowflake integration saved successfully.");
        fetchIntegration();
      }
    } catch (error) {
      Toast.error("Failed to save Snowflake integration: " + error.message);
    } finally {
    }
  };

  const handleConnection = async () => {
    setIsConnection(true);
    try {
      const response = await apiClient.get(
        `${endpoints().integrationAPI}/snowflake/connect`
      );
      if (response && response.status === 200) {
        Toast.success(response.data.message);
        // fetchMetadata(
        //   selections["database"] ||
        //     (integrationData && integrationData.other_configs.database)
        // );
        fetchIntegration();
      } else {
        Toast.error(
          (response.data && response.data.message) ||
            "Failed to connect to Snowflake."
        );
        setIsConnected(false);
      }
    } catch (error) {
      const errorMessage =
        (error.response &&
          error.response.data &&
          error.response.data.message) ||
        error.message ||
        "Failed to connect to Snowflake.";
      Toast.error("Failed to connect to Snowflake: " + errorMessage);
      setIsConnected(false);
    } finally {
      setIsConnection(false);
    }
  };

  return (
    <div className="admin-settings-section p-3">
      {/* Branded Section Header */}
      <div className="d-flex align-items-center mb-4">
        <div className="mr-3">
          <SnowflakeColorIcon width="44" height="44" />
        </div>
        <div>
          <h4 className="mb-0 font-weight-bold">Snowflake</h4>
          <p className="text-muted mb-0 small text-uppercase font-weight-bold letter-spacing-1">
            Integration Configuration
          </p>
        </div>
      </div>

      {isLoading ? (
        <div className="text-center py-5">
          <Spinner />
        </div>
      ) : (
        <Form
          initialValues={snowflakeSettingsInitialValues}
          onSubmit={values => submit(values)}
          enableReinitialize
        >
          <Card className="border-0 shadow-sm mb-2">
            <CardBody className="p-4">
              <div className="d-flex justify-content-between align-items-center mb-4 flex-wrap gap-3">
                {/* Left */}
                <div>
                  <h5 className="mb-1 font-weight-bold">
                    Connection Credentials
                  </h5>
                  <small className="text-muted">
                    Configure your Snowflake access details
                  </small>
                </div>

                {/* Right */}
                <div className="d-flex align-items-center gap-3">
                  {/* Status */}
                  <div
                    className={`status-pill rounded p-2 text-white mr-3 ${
                      isConnected
                        ? "connected bg-success"
                        : "disconnected bg-danger"
                    }`}
                  >
                    <span className="status-dot" />
                    {isConnected ? "Connected" : "Not Connected"}
                  </div>

                  {/* Button */}
                  <button
                    type="button"
                    className={`btn ${
                      isConnected ? "btn-outline-success" : "btn-primary"
                    } d-flex align-items-center px-3`}
                    onClick={handleConnection}
                    disabled={isConnection}
                  >
                    {isConnection ? (
                      <>
                        <Spinner size="sm" className="mr-2" />
                        Connecting...
                      </>
                    ) : (
                      <>
                        <i
                          className={`fas ${
                            isConnected ? "fa-sync-alt" : "fa-plug"
                          } mr-2`}
                        />
                        {isConnected ? "Test" : "Connect"}
                      </>
                    )}
                  </button>
                </div>
              </div>

              <Row>
                <Col md={12} className="mb-3">
                  <label className="font-weight-bold small text-muted text-uppercase">
                    Account Identifier
                  </label>
                  <Text
                    name="account"
                    placeholder="e.g. xy12345.us-east-1"
                    className="form-control-lg border-0"
                  />
                  <small className="text-muted mt-1 d-block">
                    Your unique Snowflake account locator.
                  </small>
                </Col>
                <Col md={6} className="mb-3">
                  <label className="font-weight-bold small text-muted text-uppercase">
                    Username
                  </label>
                  <Text
                    name={"user_name"}
                    placeholder="Username"
                    className="form-control-lg border-0"
                  />
                </Col>
                <Col md={6} className="mb-3">
                  <label className="font-weight-bold small text-muted text-uppercase">
                    Password
                  </label>
                  <Text
                    name={"password"}
                    placeholder="••••••••"
                    type="password"
                    className="form-control-lg border-0"
                  />
                </Col>
              </Row>
            </CardBody>
          </Card>
          {/* Sync Settings Card */}
          <Card className="border-0 shadow-sm mb-2">
            <CardBody className="p-4">
              <h5 className="mb-4 font-weight-bold">Sync Settings</h5>
              <small className="text-muted mb-2 d-block">
                Configure which data to sync with Snowflake
              </small>
              <div className="col-6 mb-1">
                <div className="field-wrapper">
                  <SingleCheckbox
                    name="sync_partner"
                    label="Sync Partner"
                    className="accepted-terms mt-3 mb-0 pb-0 mr-3"
                  />
                </div>
                <div className="field-wrapper">
                  <SingleCheckbox
                    name="sync_referral"
                    label="Sync Referral"
                    className="accepted-terms mt-3 mb-0 pb-0 mr-3"
                  />
                </div>
                <div className="field-wrapper">
                  <SingleCheckbox
                    name="sync_activity"
                    label="Sync Activity"
                    className="accepted-terms mt-3 mb-0 pb-0 mr-3"
                  />
                </div>
              </div>
            </CardBody>
          </Card>
          {/* Configurations */}
          {/* <Card className="border-0 shadow-sm mb-4">
          <CardBody className="p-4">
            <h5 className="font-weight-bold">Environment Configuration</h5>
            {!isConnected && (
              <div className="alert alert-light small text-muted">
                Please connect to your account to populate available resources.
              </div>
            )}
            <Row>
              <Col md={6} className="mb-4">
                <label className="font-weight-bold small text-muted text-uppercase">Default Warehouse</label>
                <Select
                  fullWidth
                  menuPortalTarget={document.body}
                  name={"warehouse"}
                  options={metadata.warehouses}
                  isLoading={metaLoading}
                  handleChange={val => handleDropdownChange("warehouse", val && val.value)}
                  placeholder="Select Warehouse"
                />
              </Col>
              <Col md={6} className="mb-4">
                <label className="font-weight-bold small text-muted text-uppercase">Primary Database</label>
                <Select
                  fullWidth
                  menuPortalTarget={document.body}
                  name={"database"}
                  options={metadata.databases}
                  isLoading={metaLoading}
                  handleChange={val => handleDropdownChange("database", val && val.value)}
                  placeholder="Select Database"
                />
              </Col>
              <Col md={6} className="mb-4">
                <label className="font-weight-bold small text-muted text-uppercase">Schema</label>
                <Select
                  fullWidth
                  menuPortalTarget={document.body}
                  name={"schema"}
                  options={metadata.schemas}
                  isLoading={metaLoading}
                  handleChange={val => handleDropdownChange("schema", val && val.value)}
                  placeholder="Select Schema"
                />
              </Col>
              <Col md={6} className="mb-4">
                <label className="font-weight-bold small text-muted text-uppercase">Assigned Role</label>
                <Select
                  fullWidth
                  menuPortalTarget={document.body}
                  name={"role"}
                  options={metadata.roles}
                  isLoading={metaLoading}
                  handleChange={val => handleDropdownChange("role", val && val.value)}
                  placeholder="Select Role"
                />
              </Col>
            </Row>
          </CardBody>
        </Card> */}
          <div className="text-right">
            <SaveButton className="btn-lg px-5 shadow-sm" />
          </div>
        </Form>
      )}
    </div>
  );
};

export default SnowflakeSection;
